/**
 * SecretFieldView Component - Read-only view of secret field
 */

import React, { useState } from 'react';
import classNames from 'classnames/bind';
import { SecretFieldViewProps } from './types';
import { copyToClipboard } from './secretFieldUtils';
import { useDecryptedContent } from './useDecryptedContent';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { resolveNoteKey } from '@/services/NoteKeyResolver';
import { getCurrentNoteId } from './secretFieldContext';
import styles from './SecretField.module.scss';

const cx = classNames.bind(styles);

const SecretFieldView: React.FC<SecretFieldViewProps> = ({
  nodeKey,
  label,
  content,
  type,
  isRevealed,
  onToggle,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const isConfigured    = useMasterPasswordStore((s) => s.isConfigured);
  const openModal       = useMasterPasswordStore((s) => s.openModal);
  const openUnlockModal = useMasterPasswordStore((s) => s.openUnlockModal);

  // Decrypt content for display
  const { plaintext, isEncrypted, decryptionFailed } = useDecryptedContent(content);
  const isLocked = isEncrypted && plaintext === null;
  const displayContent = plaintext ?? '';

  /** Attempt to load the note key after the user unlocks/sets up the master key. */
  const tryResolveAfterUnlock = () => {
    const noteId = getCurrentNoteId();
    if (noteId) {
      void resolveNoteKey(noteId);
    }
  };

  /** Click on "Unlock to view" — open appropriate modal then resolve key. */
  const handleUnlock = () => {
    if (!isConfigured) {
      openModal(tryResolveAfterUnlock);
    } else {
      openUnlockModal(tryResolveAfterUnlock);
    }
  };

  const handleCopy = async () => {
    if (isLocked) return;
    const success = await copyToClipboard(displayContent);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  const maskedContent = displayContent.length > 0 ? '*'.repeat(Math.min(displayContent.length, 12)) : '***';
  const hasLabel = label && label.trim().length > 0;

  const lockedPlaceholder = decryptionFailed
    ? '⚠ Decryption failed'
    : '🔒 Unlock to view';

  return (
    <div
      className={cx('secret-field-view', `secret-field-view--${type}`, {
        'secret-field-view--revealed': isRevealed,
      })}
      contentEditable={false}
    >
      {/* Lock Icon with label tooltip */}
      <span
        className={cx('secret-icon-wrapper')}
        title={hasLabel ? label : 'Secret field'}
      >
        <svg
          className={cx('secret-icon')}
          width={10}
          height={10}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z" />
        </svg>
      </span>

      {/* Content Display */}
      <div className={cx('secret-field-content-compact')}>
        {isLocked ? (
          <button
            type="button"
            onClick={handleUnlock}
            className={cx('content-masked', 'content-preview--locked', 'unlock-button')}
            title="Click to unlock"
          >
            {lockedPlaceholder}
          </button>
        ) : isRevealed ? (
          <span className={cx('content-revealed-compact')}>{displayContent}</span>
        ) : (
          <span className={cx('content-masked')}>{maskedContent}</span>
        )}
      </div>

      {/* Buttons - absolute positioned */}
      <div className={cx('secret-field-buttons')}>
        {/* Toggle Button — hidden when locked */}
        {!isLocked && (
          <button
            type="button"
            onClick={onToggle}
            className={cx('icon-button-compact', 'toggle-button', 'button-hover-only')}
            title={isRevealed ? 'Hide' : 'Show'}
          >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            {isRevealed ? (
              // Eye slash (hide)
              <path d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.08L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.74,7.13 11.35,7 12,7Z" />
            ) : (
              // Eye (show)
              <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
            )}
          </svg>
        </button>
        )}

        {/* Copy Button (only when revealed and not locked) */}
        {isRevealed && !isLocked && (
          <button
            type="button"
            onClick={handleCopy}
            className={cx('icon-button-compact', 'copy-button', 'button-hover-only', {
              'copy-button--success': copySuccess,
            })}
            title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              {copySuccess ? (
                // Check icon
                <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
              ) : (
                // Copy icon
                <path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
              )}
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default SecretFieldView;
